import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Server, AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react';
import { Spinner, Banner } from '../../design-system';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { ApplicationCreate, ApplicationRepository } from '../../types/application';
import { useQuotaBalance } from '../../hooks/useQuotaBalance';
import toast from '../../design-system/toast';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { useAuth } from '../../contexts/AuthContext';
import { useEnabledProviders } from '../../hooks/useEnabledProviders';
import { getRepositoryKey, parseRepositoryKey } from '../../utils/repository';
import { GitProvider, ProviderIcon, isFormProvider, providerLabel } from '../../config/providers';
import {
  CodeCommitConnection,
  OrgCodeCommitStatus,
  codecommitConnectionDisplayName,
  codecommitConsoleUrl,
} from '../../types/codecommit';

interface Repository {
  full_name: string;
  private: boolean;
  default_branch: string;
  html_url?: string;
  provider: GitProvider;
  provider_repository_id?: string;
  gitlab_instance_url?: string;
}

interface ProviderConnectionStatus {
  provider: GitProvider;
  connected: boolean;
  username?: string;
  // GitHub only: deep-link to the installation settings where repo access is granted.
  manageUrl?: string;
  error?: string;
}

interface GitHubStatusResponse {
  connected: boolean;
  github_username?: string;
  installation_id?: number;
  manage_url?: string;
}

interface GitLabConnectionItem {
  gitlab_instance_url: string;
  gitlab_username?: string;
}

interface GitLabStatusResponse {
  connected: boolean;
  gitlab_username?: string;
  connections?: GitLabConnectionItem[];
}

interface GitLabPaginatedResponse {
  repositories: Array<Omit<Repository, 'provider'>>;
  pagination: {
    page: number;
    per_page: number;
    total: number | null;
    has_more: boolean;
  };
}

interface AzureDevOpsStatusResponse {
  connected: boolean;
  ado_organization_url?: string;
}

interface CreatedApplication {
  id: string;
  name: string;
}

type GitLabRepositoryDiscoveryMode = 'membership' | 'discover';
type ApplicationCreationMode = 'connected' | 'public_github';
// 'single_repo' constrains creation to exactly one repository and derives the
// application name from it. 'onboarding_repo_only' is the original alias for the
// same behavior, kept so the onboarding flow keeps working unchanged.
type ApplicationFormVariant = 'standard' | 'onboarding_repo_only' | 'single_repo';

// Providers this form can list repositories for. A provider that is enabled for the
// deployment but not listed here has no picker implementation yet and is not offered.
const REPO_PICKER_PROVIDERS: readonly GitProvider[] = ['github', 'gitlab', 'azure_devops', 'codecommit'];

const EMPTY_REPO_CACHE: Record<GitProvider, Repository[] | null> = {
  github: null,
  gitlab: null,
  azure_devops: null,
  codecommit: null,
};
const NO_PROVIDER_FLAGS: Record<GitProvider, boolean> = {
  github: false,
  gitlab: false,
  azure_devops: false,
  codecommit: false,
};

function gitlabInstanceDisplayName(url: string): string {
  try {
    return new URL(url).hostname || url;
  } catch {
    return url;
  }
}

function normalizeNamespace(value: string): string {
  return value.trim().replace(/^\/+|\/+$/g, '');
}

function deriveApplicationNameFromRepository(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  let repoPath = trimmed;
  try {
    const parsedUrl = new URL(trimmed);
    repoPath = parsedUrl.pathname;
  } catch {
    repoPath = trimmed;
  }

  const cleanedPath = repoPath
    .replace(/^\/+|\/+$/g, '')
    .replace(/\.git$/i, '');
  const pathSegments = cleanedPath.split('/').filter(Boolean);
  return (pathSegments[pathSegments.length - 1] || cleanedPath || trimmed).slice(0, 100);
}

function buildGitlabDiscoveryQuery(searchText?: string): {
  discoveryMode: GitLabRepositoryDiscoveryMode;
  search?: string;
  namespace?: string;
} {
  const trimmedSearchText = (searchText || '').trim();
  if (!trimmedSearchText) {
    return { discoveryMode: 'membership' };
  }

  const lowercaseSearchText = trimmedSearchText.toLowerCase();
  if (lowercaseSearchText.startsWith('namespace:')) {
    const rawNamespace = trimmedSearchText.slice('namespace:'.length);
    const namespace = normalizeNamespace(rawNamespace);
    return {
      discoveryMode: 'discover',
      namespace: namespace || undefined,
    };
  }

  if (lowercaseSearchText.startsWith('ns:')) {
    const rawNamespace = trimmedSearchText.slice('ns:'.length);
    const namespace = normalizeNamespace(rawNamespace);
    return {
      discoveryMode: 'discover',
      namespace: namespace || undefined,
    };
  }

  if (trimmedSearchText.endsWith('/')) {
    const namespace = normalizeNamespace(trimmedSearchText);
    return {
      discoveryMode: 'discover',
      namespace: namespace || undefined,
    };
  }

  return {
    discoveryMode: 'discover',
    search: trimmedSearchText,
  };
}

export interface ApplicationFormSubmitMeta {
  repoCount: number;
  hasDescription: boolean;
  repoProviders: string[];
  privateRepoCount: number;
}

export interface ApplicationFormProps {
  organizationId: string;
  /** Fired when the create-application API call succeeds. Receives submission metadata. */
  onSuccess: (application: CreatedApplication, meta: ApplicationFormSubmitMeta) => void;
  onCancel?: () => void;
  /** Custom label for submit button */
  submitLabel?: string;
  /** Custom label for cancel button */
  cancelLabel?: string;
  /** Whether to auto-focus the name input */
  autoFocus?: boolean;
  /** Custom redirect URL for GitHub OAuth callback */
  githubRedirectTo?: string;
  /** Fired when the create-application API call rejects. Receives submission metadata. */
  onError?: (error: unknown, meta: ApplicationFormSubmitMeta) => void;
  /** Form variant. Onboarding derives app details from a single selected repository. */
  variant?: ApplicationFormVariant;
}

const ApplicationForm: React.FC<ApplicationFormProps> = ({
  organizationId,
  onSuccess,
  onCancel,
  submitLabel = 'Create Repository',
  cancelLabel = 'Cancel',
  autoFocus = true,
  githubRedirectTo,
  onError,
  variant = 'standard',
}) => {
  const api = useAuthenticatedApi();
  const enabledProviders = useEnabledProviders();
  const pickerProviders = useMemo(
    () => enabledProviders.filter((p) => REPO_PICKER_PROVIDERS.includes(p)),
    [enabledProviders]
  );
  const { isAdmin } = useAuth();
  const { openOrgSettings, openAccountSettings } = useSettingsModal();
  const { quotaBalance } = useQuotaBalance();

  const appLimitReached = quotaBalance !== null
    && quotaBalance.max_applications !== null
    && quotaBalance.current_applications >= quotaBalance.max_applications;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creationMode, setCreationMode] = useState<ApplicationCreationMode>('connected');
  const [publicRepoInput, setPublicRepoInput] = useState('');
  const [publicRepoBranch, setPublicRepoBranch] = useState('');
  const [selectedRepos, setSelectedRepos] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Git provider state
  const [providerStatuses, setProviderStatuses] = useState<ProviderConnectionStatus[]>([]);
  const [repoCache, setRepoCache] = useState<Record<GitProvider, Repository[] | null>>(EMPTY_REPO_CACHE);
  const [loadingGit, setLoadingGit] = useState(true);
  const [loadingRepos, setLoadingRepos] = useState<Record<GitProvider, boolean>>(NO_PROVIDER_FLAGS);
  const [adoRepoError, setAdoRepoError] = useState<string | null>(null);
  // True when a repo fetch *failed* (vs. genuinely returned zero repos). Lets the
  // empty state distinguish "couldn't load" from "connected but no access granted".
  // ADO uses adoRepoError for the same purpose, so it's not tracked here.
  const [repoLoadFailed, setRepoLoadFailed] = useState<Record<GitProvider, boolean>>(NO_PROVIDER_FLAGS);
  const [repoSearch, setRepoSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState<GitProvider | null>(null);

  // GitLab pagination state
  const [gitlabPage, setGitlabPage] = useState(1);
  const [gitlabHasMore, setGitlabHasMore] = useState(true);

  // Azure DevOps project-scoped state
  const [adoProjects, setAdoProjects] = useState<string[]>([]);
  const [selectedAdoProject, setSelectedAdoProject] = useState<string | null>(null);
  // AWS CodeCommit connection-scoped state (one connection = one account + region)
  const [codecommitConnections, setCodecommitConnections] = useState<CodeCommitConnection[]>([]);
  const [selectedCodecommitConnectionId, setSelectedCodecommitConnectionId] = useState<string | null>(null);
  const [codecommitRepoError, setCodecommitRepoError] = useState<string | null>(null);
  const [gitlabConnections, setGitlabConnections] = useState<GitLabConnectionItem[]>([]);
  const [selectedGitlabInstanceUrl, setSelectedGitlabInstanceUrl] = useState<string | undefined>(undefined);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const isPublicRepoMode = isAdmin && creationMode === 'public_github';
  const isSingleRepo = variant === 'onboarding_repo_only' || variant === 'single_repo';

  // Helper to check if a specific provider is connected
  const isProviderConnected = (provider: GitProvider) =>
    providerStatuses.find((p) => p.provider === provider)?.connected ?? false;

  // Current repos from cache (memoized to avoid unnecessary re-renders)
  const repos = useMemo(
    () => (providerFilter ? repoCache[providerFilter] ?? [] : []),
    [providerFilter, repoCache]
  );

  // CodeCommit is feature-gated: its status endpoint 404s when the flag is off, so only
  // ask for it once the provider is offered. A stable boolean keeps the effect below from
  // re-running on every feature-store change.
  const includeCodecommit = pickerProviders.includes('codecommit');

  // Load provider statuses on mount (parallel lightweight requests)
  useEffect(() => {
    if (!organizationId) return;

    let cancelled = false;

    const loadProviderStatuses = async () => {
      setLoadingGit(true);
      try {
        // Fetch all statuses in parallel - these are lightweight endpoints
        const [githubStatus, gitlabStatus, adoStatus, codecommitStatus] = await Promise.all([
          api.get<GitHubStatusResponse>(apiEndpoints.orgGithub.status(organizationId)).catch(() => null),
          api.get<GitLabStatusResponse>(apiEndpoints.orgGitlab.status(organizationId)).catch(() => null),
          api.get<AzureDevOpsStatusResponse>(apiEndpoints.orgAzureDevOps.status(organizationId)).catch(() => null),
          includeCodecommit
            ? api.get<OrgCodeCommitStatus>(apiEndpoints.orgCodeCommit.status(organizationId)).catch(() => null)
            : Promise.resolve<OrgCodeCommitStatus | null>(null),
        ]);

        if (cancelled) return;

        const connections = gitlabStatus?.connections ?? [];
        setGitlabConnections(connections);
        setSelectedGitlabInstanceUrl((prev) => (prev ?? (connections[0]?.gitlab_instance_url || undefined)));

        const ccConnections = codecommitStatus?.connections ?? [];
        setCodecommitConnections(ccConnections);
        setSelectedCodecommitConnectionId((prev) =>
          prev && ccConnections.some((c) => c.id === prev) ? prev : ccConnections[0]?.id ?? null
        );

        const statuses: ProviderConnectionStatus[] = [
          {
            provider: 'github',
            connected: githubStatus?.connected ?? false,
            username: githubStatus?.github_username,
            manageUrl: githubStatus?.manage_url,
          },
          {
            provider: 'gitlab',
            connected: gitlabStatus?.connected ?? false,
            username: gitlabStatus?.gitlab_username,
          },
          {
            provider: 'azure_devops',
            connected: adoStatus?.connected ?? false,
            username: adoStatus?.ado_organization_url,
          },
          {
            provider: 'codecommit',
            connected: (codecommitStatus?.connected ?? false) && ccConnections.length > 0,
            username: ccConnections[0] ? codecommitConnectionDisplayName(ccConnections[0]) : undefined,
          },
        ];
        setProviderStatuses(statuses);

        // Auto-select first connected provider (keep the user's choice on a re-run)
        const autoSelected: GitProvider = githubStatus?.connected
          ? 'github'
          : gitlabStatus?.connected
            ? 'gitlab'
            : adoStatus?.connected
              ? 'azure_devops'
              : ccConnections.length > 0
                ? 'codecommit'
                : 'github'; // Default to github if no provider connected
        setProviderFilter((prev) => prev ?? autoSelected);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load provider statuses:', err);
        setProviderStatuses([]);
        setProviderFilter('github');
      } finally {
        if (!cancelled) {
          setLoadingGit(false);
        }
      }
    };

    loadProviderStatuses();

    return () => {
      cancelled = true;
    };
  }, [api, organizationId, includeCodecommit]);

  // Debounce search input for GitLab (400ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(repoSearch);
    }, 400);
    return () => clearTimeout(timer);
  }, [repoSearch]);

  // Reset GitLab pagination when debounced search changes
  useEffect(() => {
    if (providerFilter === 'gitlab') {
      setGitlabPage(1);
      setGitlabHasMore(true);
      setRepoCache((prev) => ({ ...prev, gitlab: null }));
    }
  }, [debouncedSearch, providerFilter]);

  // Load GitLab repositories with pagination
  const loadGitlabRepos = useCallback(
    async (page: number, search?: string, append = false) => {
      if (page > 1) {
        setIsLoadingMore(true);
      } else {
        setLoadingRepos((prev) => ({ ...prev, gitlab: true }));
      }

      try {
        const discoveryQuery = buildGitlabDiscoveryQuery(search);
        const params = new URLSearchParams({
          page: String(page),
          per_page: '30',
        });
        if (discoveryQuery.search) {
          params.set('search', discoveryQuery.search);
        }

        const base = apiEndpoints.orgGitlab.repos(organizationId, {
          instanceUrl: selectedGitlabInstanceUrl,
          discoveryMode: discoveryQuery.discoveryMode,
          namespace: discoveryQuery.namespace,
        });
        const url = base.includes('?') ? `${base}&${params}` : `${base}?${params}`;
        const response = await api.get<GitLabPaginatedResponse>(url);

        const repos = response.repositories.map((r) => ({
          ...r,
          provider: 'gitlab' as const,
        }));

        setRepoCache((prev) => ({
          ...prev,
          gitlab: append ? [...(prev.gitlab || []), ...repos] : repos,
        }));
        setGitlabHasMore(response.pagination.has_more);
        setGitlabPage(page);
        if (!append) {
          setRepoLoadFailed((prev) => ({ ...prev, gitlab: false }));
        }
      } catch (err) {
        console.error('Failed to load GitLab repositories:', err);
        if (!append) {
          setRepoCache((prev) => ({ ...prev, gitlab: [] }));
          setRepoLoadFailed((prev) => ({ ...prev, gitlab: true }));
        }
      } finally {
        setIsLoadingMore(false);
        setLoadingRepos((prev) => ({ ...prev, gitlab: false }));
      }
    },
    [api, organizationId, selectedGitlabInstanceUrl]
  );

  // Load GitHub repositories (unchanged - fetches all at once)
  const loadGithubRepos = useCallback(async () => {
    setLoadingRepos((prev) => ({ ...prev, github: true }));
    try {
      const data = await api.get<Array<Omit<Repository, 'provider'>>>(
        apiEndpoints.orgGithub.repos(organizationId)
      );
      const fetchedRepos = data.map((r) => ({ ...r, provider: 'github' as const }));
      setRepoCache((prev) => ({ ...prev, github: fetchedRepos }));
      setRepoLoadFailed((prev) => ({ ...prev, github: false }));
    } catch (err) {
      console.error('Failed to load GitHub repositories:', err);
      setRepoCache((prev) => ({ ...prev, github: [] }));
      setRepoLoadFailed((prev) => ({ ...prev, github: true }));
    } finally {
      setLoadingRepos((prev) => ({ ...prev, github: false }));
    }
  }, [api, organizationId]);

  // Load ADO projects for the project picker
  const loadAdoProjects = useCallback(async () => {
    setLoadingRepos((prev) => ({ ...prev, azure_devops: true }));
    try {
      const data = await api.get<string[]>(apiEndpoints.orgAzureDevOps.projects(organizationId));
      setAdoProjects(data);
      if (data.length === 1) setSelectedAdoProject(data[0]);
    } catch (err: any) {
      console.error('Failed to load Azure DevOps projects:', err);
      if (err?.status === 401) {
        setAdoRepoError('Azure DevOps PAT is invalid or revoked. Go to Organization Settings → Integrations to reconnect.');
      }
    } finally {
      setLoadingRepos((prev) => ({ ...prev, azure_devops: false }));
    }
  }, [api, organizationId]);

  // Load all repos for a single ADO project
  const loadAdoRepos = useCallback(
    async (project: string) => {
      setLoadingRepos((prev) => ({ ...prev, azure_devops: true }));
      setAdoRepoError(null);
      try {
        const data = await api.get<Array<Omit<Repository, 'provider'>>>(
          apiEndpoints.orgAzureDevOps.repos(organizationId, project)
        );
        setRepoCache((prev) => ({
          ...prev,
          azure_devops: data.map((r) => ({ ...r, provider: 'azure_devops' as const })),
        }));
      } catch (err: any) {
        setRepoCache((prev) => ({ ...prev, azure_devops: [] }));
        if (err?.status === 401) {
          setAdoRepoError('Azure DevOps PAT is invalid or revoked. Go to Organization Settings → Integrations to reconnect.');
        }
      } finally {
        setLoadingRepos((prev) => ({ ...prev, azure_devops: false }));
      }
    },
    [api, organizationId]
  );

  const handleAdoProjectSelect = (project: string) => {
    setSelectedAdoProject(project);
    setRepoCache((prev) => ({ ...prev, azure_devops: null }));
  };

  // Load all repos for one AWS CodeCommit connection (account + region)
  const loadCodecommitRepos = useCallback(
    async (connectionId: string) => {
      setLoadingRepos((prev) => ({ ...prev, codecommit: true }));
      setCodecommitRepoError(null);
      try {
        const data = await api.get<Array<Omit<Repository, 'provider'>>>(
          apiEndpoints.orgCodeCommit.repos(organizationId, { connectionId })
        );
        setRepoCache((prev) => ({
          ...prev,
          codecommit: data.map((r) => ({ ...r, provider: 'codecommit' as const })),
        }));
        setRepoLoadFailed((prev) => ({ ...prev, codecommit: false }));
      } catch (err: any) {
        console.error('Failed to load AWS CodeCommit repositories:', err);
        setRepoCache((prev) => ({ ...prev, codecommit: [] }));
        if (err?.status === 401 || err?.status === 403) {
          setCodecommitRepoError(
            'AWS CodeCommit credentials are invalid or revoked. Go to Organization Settings → Integrations to reconnect.'
          );
        } else {
          setRepoLoadFailed((prev) => ({ ...prev, codecommit: true }));
        }
      } finally {
        setLoadingRepos((prev) => ({ ...prev, codecommit: false }));
      }
    },
    [api, organizationId]
  );

  const handleCodecommitConnectionSelect = (connectionId: string) => {
    setSelectedCodecommitConnectionId(connectionId);
    setCodecommitRepoError(null);
    setRepoCache((prev) => ({ ...prev, codecommit: null }));
  };

  // Force a re-fetch of the selected provider's repos (used by the empty-state
  // "Refresh" action and the auto-refresh-on-return effect). Calls the loaders
  // directly so it bypasses the "already cached" guards.
  const refreshRepos = useCallback(
    (provider: GitProvider) => {
      if (provider === 'github') {
        setRepoLoadFailed((p) => ({ ...p, github: false }));
        loadGithubRepos();
      } else if (provider === 'gitlab') {
        setRepoLoadFailed((p) => ({ ...p, gitlab: false }));
        setGitlabPage(1);
        setGitlabHasMore(true);
        loadGitlabRepos(1, debouncedSearch || undefined, false);
      } else if (provider === 'azure_devops') {
        // Azure DevOps: re-fetch the selected project's repos, or the project list itself.
        setAdoRepoError(null);
        if (selectedAdoProject) {
          loadAdoRepos(selectedAdoProject);
        } else {
          loadAdoProjects();
        }
      } else if (provider === 'codecommit') {
        setRepoLoadFailed((p) => ({ ...p, codecommit: false }));
        if (selectedCodecommitConnectionId) loadCodecommitRepos(selectedCodecommitConnectionId);
      }
    },
    [
      loadGithubRepos,
      loadGitlabRepos,
      loadAdoRepos,
      loadAdoProjects,
      loadCodecommitRepos,
      selectedAdoProject,
      selectedCodecommitConnectionId,
      debouncedSearch,
    ]
  );

  // Load GitHub/GitLab repositories when provider filter changes
  useEffect(() => {
    if (!organizationId || !providerFilter) return;
    const isConnected = providerStatuses.find((p) => p.provider === providerFilter)?.connected;
    if (!isConnected) return;
    if (repoCache[providerFilter] !== null) return;

    if (providerFilter === 'github') {
      loadGithubRepos();
    } else if (providerFilter === 'gitlab') {
      loadGitlabRepos(1, debouncedSearch || undefined, false);
    }
    // ADO repos are loaded via project selection (see effects below)
  }, [organizationId, providerFilter, providerStatuses, repoCache, debouncedSearch, loadGithubRepos, loadGitlabRepos]);

  // Load ADO projects when Azure DevOps tab is first selected
  useEffect(() => {
    if (providerFilter !== 'azure_devops' || !organizationId) return;
    const isConnected = providerStatuses.find((p) => p.provider === 'azure_devops')?.connected;
    if (!isConnected || adoProjects.length > 0) return;
    loadAdoProjects();
  }, [providerFilter, organizationId, providerStatuses, adoProjects.length, loadAdoProjects]);

  // Load ADO repos when a project is selected and cache is empty
  useEffect(() => {
    if (providerFilter !== 'azure_devops' || !selectedAdoProject || !organizationId) return;
    if (repoCache.azure_devops !== null) return;
    loadAdoRepos(selectedAdoProject);
  }, [providerFilter, selectedAdoProject, organizationId, repoCache.azure_devops, loadAdoRepos]);

  // Load CodeCommit repos once a connection is selected
  useEffect(() => {
    if (providerFilter !== 'codecommit' || !selectedCodecommitConnectionId || !organizationId) return;
    const isConnected = providerStatuses.find((p) => p.provider === 'codecommit')?.connected;
    if (!isConnected || repoCache.codecommit !== null) return;
    loadCodecommitRepos(selectedCodecommitConnectionId);
  }, [
    providerFilter,
    selectedCodecommitConnectionId,
    organizationId,
    providerStatuses,
    repoCache.codecommit,
    loadCodecommitRepos,
  ]);

  // Smoothness touch: when the user grants repo access in another tab and returns
  // here, auto-refresh so the list populates without them hunting for the button.
  // Only fires while we're actually showing an empty/no-access state.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !providerFilter) return;
      const connected = providerStatuses.find((p) => p.provider === providerFilter)?.connected;
      if (!connected || loadingRepos[providerFilter]) return;
      const isEmpty =
        providerFilter === 'azure_devops'
          ? selectedAdoProject
            ? repoCache.azure_devops !== null && repoCache.azure_devops.length === 0
            : adoProjects.length === 0
          : (() => {
              const cache = repoCache[providerFilter];
              return cache !== null && cache.length === 0;
            })();
      if (isEmpty) refreshRepos(providerFilter);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [providerFilter, providerStatuses, repoCache, loadingRepos, adoProjects.length, selectedAdoProject, refreshRepos]);

  // IntersectionObserver for GitLab infinite scroll
  useEffect(() => {
    if (providerFilter !== 'gitlab' || !gitlabHasMore || isLoadingMore || loadingRepos.gitlab) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadGitlabRepos(gitlabPage + 1, debouncedSearch || undefined, true);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [providerFilter, gitlabHasMore, gitlabPage, isLoadingMore, loadingRepos.gitlab, debouncedSearch, loadGitlabRepos]);

  // Client-side search filtering for GitHub and ADO; GitLab uses server-side search
  const filteredRepos = useMemo(() => {
    if (providerFilter === 'gitlab') return repos;
    if (!repoSearch) return repos;
    const query = repoSearch.toLowerCase();
    return repos.filter((repo) => repo.full_name.toLowerCase().includes(query));
  }, [repos, repoSearch, providerFilter]);

  const handleConnectGithub = async () => {
    try {
      const connectUrl = githubRedirectTo
        ? `${apiEndpoints.orgGithub.connect(organizationId)}?redirect_to=${encodeURIComponent(githubRedirectTo)}`
        : apiEndpoints.orgGithub.connect(organizationId);
      const response = await api.get<{ authorization_url: string }>(connectUrl);
      if (response?.authorization_url) {
        window.location.href = response.authorization_url;
      }
    } catch (err) {
      console.error('Failed to initiate organization GitHub connection:', err);
      toast.error('Failed to connect to GitHub');
    }
  };

  const handleConnectGitlab = async () => {
    try {
      const connectUrl = githubRedirectTo
        ? `${apiEndpoints.orgGitlab.connect(organizationId)}?redirect_to=${encodeURIComponent(githubRedirectTo)}`
        : apiEndpoints.orgGitlab.connect(organizationId);
      const response = await api.get<{ authorization_url: string }>(connectUrl);
      if (response?.authorization_url) {
        window.location.href = response.authorization_url;
      }
    } catch (err) {
      console.error('Failed to initiate organization GitLab connection:', err);
      toast.error('Failed to connect to GitLab');
    }
  };

  const toggleRepo = (provider: GitProvider, fullName: string) => {
    const key = getRepositoryKey(provider, fullName);
    setSelectedRepos((prev) => {
      if (isSingleRepo) {
        return new Set([key]);
      }

      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const selectedRepositories = useMemo(() => {
    return Array.from(selectedRepos)
    .map((repoKey) => {
      const { provider, fullName } = parseRepositoryKey(repoKey);
      const cachedRepo = (repoCache[provider] ?? []).find((repo) => repo.full_name === fullName);
      return {
        repoKey,
        provider,
        fullName,
        cachedRepo,
      };
    })
    .sort((left, right) => left.fullName.localeCompare(right.fullName));
  }, [selectedRepos, repoCache]);

  const handleProviderSwitch = (provider: GitProvider) => {
    if (provider !== providerFilter) {
      setRepoSearch('');
      setDebouncedSearch('');
      if (provider === 'gitlab') {
        setGitlabPage(1);
        setGitlabHasMore(true);
      }
      setProviderFilter(provider);
    }
  };

  const handleSubmit = async () => {
    if (!isSingleRepo && !name.trim()) {
      setError('Application name is required');
      return;
    }
    if (isSingleRepo && !isPublicRepoMode && selectedRepos.size === 0) {
      setError('Select a repository to continue');
      return;
    }
    if (isSingleRepo && isPublicRepoMode && !publicRepoInput.trim()) {
      setError('Repository is required');
      return;
    }

    const meta: ApplicationFormSubmitMeta = {
      repoCount: isPublicRepoMode && publicRepoInput.trim() ? 1 : selectedRepos.size,
      hasDescription: isSingleRepo ? false : description.trim().length > 0,
      repoProviders: isPublicRepoMode
        ? ['github']
        : Array.from(new Set(selectedRepositories.map((repo) => repo.provider))).sort(),
      privateRepoCount: isPublicRepoMode
        ? 0
        : selectedRepositories.filter((repo) => repo.cachedRepo?.private).length,
    };

    try {
      setIsSubmitting(true);
      setError(null);

      if (isPublicRepoMode) {
        if (!publicRepoInput.trim()) {
          setError('Repository is required');
          return;
        }

        const publicRepositoryApplicationName = isSingleRepo
          ? deriveApplicationNameFromRepository(publicRepoInput)
          : name.trim();
        const publicRepositoryPayload: {
          name: string;
          description?: string;
          repository: string;
          default_branch?: string;
        } = {
          name: publicRepositoryApplicationName,
          repository: publicRepoInput.trim(),
          default_branch: publicRepoBranch.trim() || undefined,
        };
        if (!isSingleRepo && description.trim()) {
          publicRepositoryPayload.description = description.trim();
        }

        const createdApp = await api.post<CreatedApplication>(
          apiEndpoints.applications.createPublicGithub(organizationId),
          publicRepositoryPayload
        );
        toast.success('Repository added');
        onSuccess(createdApp, meta);
        return;
      }

      const repositories: ApplicationRepository[] = Array.from(selectedRepos).map((repoKey) => {
        const { provider, fullName } = parseRepositoryKey(repoKey);

        const cachedRepos = repoCache[provider] ?? [];
        const repo = cachedRepos.find((r) => r.full_name === fullName);
        return {
          full_name: fullName,
          default_branch: repo?.default_branch || 'main',
          html_url: repo?.html_url,
          provider: repo?.provider || provider,
          provider_repository_id: repo?.provider_repository_id,
          gitlab_instance_url: repo?.gitlab_instance_url,
          pr_scan_enabled: false,
          pr_scan_mode: 'partial',
          pr_scan_engines: ['secrets', 'semgrep', 'ai'],
        };
      });

      const selectedRepositoryName = selectedRepositories[0]?.fullName || '';
      const data: ApplicationCreate = {
        name: isSingleRepo ? deriveApplicationNameFromRepository(selectedRepositoryName) : name.trim(),
        repositories,
      };
      if (!isSingleRepo && description.trim()) {
        data.description = description.trim();
      }

      const createdApp = await api.post<CreatedApplication>(apiEndpoints.applications.create(organizationId), data);
      toast.success('Repository added');
      onSuccess(createdApp, meta);
    } catch (err: any) {
      console.error('Failed to add repository:', err);
      const errorMessage = err.data?.detail || err.message || 'Failed to add repository';
      onError?.(err, meta);
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Renders the message shown when the repo list is empty. Distinguishes a genuine
  // load failure from "connected but no repo access granted", and gives a tailored
  // way out per provider. Every fix-it link opens in a NEW TAB so onboarding is never
  // navigated away; the user resolves access there and returns to "Refresh" in place.
  const renderEmptyRepoState = () => {
    const actionBtn =
      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ' +
      'border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary ' +
      'hover:bg-ui-background-tertiary transition-colors whitespace-nowrap';
    const integrationsHref = `/organizations/${organizationId}/settings/integrations`;
    const refreshBtn = (provider: GitProvider) => (
      <button type="button" onClick={() => refreshRepos(provider)} className={actionBtn}>
        <RefreshCw size={13} /> Refresh
      </button>
    );

    // Azure DevOps is PAT-based and project-scoped, so it has its own sub-cases.
    if (providerFilter === 'azure_devops') {
      if (adoRepoError) {
        return (
          <Banner
            variant="error"
            icon={<AlertTriangle size={18} />}
            title="Couldn't connect to Azure DevOps"
          >
            <div>{adoRepoError}</div>
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              <a href={integrationsHref} target="_blank" rel="noreferrer" className={actionBtn}>
                <ExternalLink size={13} /> Update token
              </a>
              {refreshBtn('azure_devops')}
            </div>
          </Banner>
        );
      }
      // Case (A): connected, but the token can't see any projects.
      if (adoProjects.length === 0) {
        return (
          <Banner variant="warning" icon={<AlertTriangle size={18} />} title="No projects available">
            <div>
              You're connected to Azure DevOps, but no projects are accessible with this token.
              Re-connect with a token that has project and code read access, then refresh.
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              <a href={integrationsHref} target="_blank" rel="noreferrer" className={actionBtn}>
                <ExternalLink size={13} /> Update token
              </a>
              {refreshBtn('azure_devops')}
            </div>
          </Banner>
        );
      }
      // Projects exist but none chosen yet — legitimate, keep the simple hint.
      if (!selectedAdoProject) {
        return (
          <p className="text-sm text-ui-text-tertiary text-center py-4">
            Select a project above to view repositories
          </p>
        );
      }
      // Case (B): project selected, but it has no Git repositories (200 + empty).
      if (repoCache.azure_devops !== null && repoCache.azure_devops.length === 0) {
        return (
          <Banner
            variant="warning"
            icon={<AlertTriangle size={18} />}
            title="This project has no repositories"
          >
            <div>
              This Azure DevOps project doesn't contain any Git repositories. Pick a different project
              above, or add a repository to it in Azure DevOps, then refresh.
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2.5">{refreshBtn('azure_devops')}</div>
          </Banner>
        );
      }
      // Repos exist but the search filtered them all out.
      return (
        <p className="text-sm text-ui-text-tertiary text-center py-4">No repositories match your search</p>
      );
    }

    // AWS CodeCommit is credential-based and connection-scoped (account + region).
    if (providerFilter === 'codecommit') {
      if (codecommitRepoError) {
        return (
          <Banner
            variant="error"
            icon={<AlertTriangle size={18} />}
            title="Couldn't connect to AWS CodeCommit"
          >
            <div>{codecommitRepoError}</div>
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              <a href={integrationsHref} target="_blank" rel="noreferrer" className={actionBtn}>
                <ExternalLink size={13} /> Update credentials
              </a>
              {refreshBtn('codecommit')}
            </div>
          </Banner>
        );
      }
      if (repoLoadFailed.codecommit) {
        return (
          <Banner variant="error" icon={<AlertTriangle size={18} />} title="Couldn't load repositories">
            <div>Something went wrong fetching your repositories.</div>
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              <button type="button" onClick={() => refreshRepos('codecommit')} className={actionBtn}>
                <RefreshCw size={13} /> Try again
              </button>
            </div>
          </Banner>
        );
      }
      const connection = codecommitConnections.find((c) => c.id === selectedCodecommitConnectionId);
      if (repoCache.codecommit !== null && repoCache.codecommit.length === 0 && !repoSearch) {
        return (
          <Banner variant="warning" icon={<AlertTriangle size={18} />} title="No repositories in this region">
            <div>
              You're connected to AWS CodeCommit, but there are no repositories in
              {connection ? ` ${connection.aws_region}` : ' this region'} for this account. Create one in
              the CodeCommit console or choose another connection, then refresh.
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              {connection && (
                <a
                  href={codecommitConsoleUrl(connection.aws_region)}
                  target="_blank"
                  rel="noreferrer"
                  className={actionBtn}
                >
                  <ExternalLink size={13} /> Open CodeCommit console
                </a>
              )}
              {refreshBtn('codecommit')}
            </div>
          </Banner>
        );
      }
      return (
        <p className="text-sm text-ui-text-tertiary text-center py-4">No repositories match your search</p>
      );
    }

    if (!providerFilter) return null;
    const provider = providerFilter;

    // The fetch failed (not a genuine empty list).
    if (repoLoadFailed[provider]) {
      return (
        <Banner variant="error" icon={<AlertTriangle size={18} />} title="Couldn't load repositories">
          <div>Something went wrong fetching your repositories.</div>
          <div className="flex flex-wrap items-center gap-2 mt-2.5">
            <button type="button" onClick={() => refreshRepos(provider)} className={actionBtn}>
              <RefreshCw size={13} /> Try again
            </button>
          </div>
        </Banner>
      );
    }

    const cache = repoCache[provider];
    const connectedButEmpty = cache !== null && cache.length === 0;

    // Connected, load succeeded, but zero repos accessible — the core dead-end.
    if (connectedButEmpty && !repoSearch) {
      if (provider === 'github') {
        const manageUrl = providerStatuses.find((p) => p.provider === 'github')?.manageUrl;
        return (
          <Banner
            variant="warning"
            icon={<AlertTriangle size={18} />}
            title="Kolega can't access any of your repositories"
          >
            <div>
              You're connected to GitHub, but the app hasn't been granted access to any repositories.
              Open GitHub to give it access to at least one repository, then refresh.
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              {manageUrl ? (
                <a href={manageUrl} target="_blank" rel="noreferrer" className={actionBtn}>
                  <ExternalLink size={13} /> Configure on GitHub
                </a>
              ) : (
                <button type="button" onClick={handleConnectGithub} className={actionBtn}>
                  <ExternalLink size={13} /> Configure on GitHub
                </button>
              )}
              {refreshBtn('github')}
            </div>
          </Banner>
        );
      }
      // GitLab: connected but not a member of any project.
      const gitlabUrl = selectedGitlabInstanceUrl
        ? `${selectedGitlabInstanceUrl.replace(/\/$/, '')}/dashboard/projects`
        : undefined;
      return (
        <Banner variant="warning" icon={<AlertTriangle size={18} />} title="No projects available">
          <div>
            You're connected to GitLab, but you're not a member of any projects on this instance.
            Join or create a project, then refresh.
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2.5">
            {gitlabUrl && (
              <a href={gitlabUrl} target="_blank" rel="noreferrer" className={actionBtn}>
                <ExternalLink size={13} /> Open GitLab
              </a>
            )}
            {refreshBtn('gitlab')}
          </div>
        </Banner>
      );
    }

    // Repos exist but the search filtered them all out.
    return (
      <p className="text-sm text-ui-text-tertiary text-center py-4">No repositories match your search</p>
    );
  };

  const renderRepositorySection = () => {
    const isSelectedProviderConnected = providerFilter ? isProviderConnected(providerFilter) : false;
    const showConnectedUI = isSelectedProviderConnected || loadingGit;

    return (
      <>
        {/* Provider filter buttons - always visible */}
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">
          {pickerProviders.map((provider) => (
            <button
              key={provider}
              type="button"
              onClick={() => handleProviderSwitch(provider)}
              disabled={loadingGit}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                providerFilter === provider
                  ? 'bg-brand-primary/20 text-brand-primary dark:text-brand-primaryDarkMode border border-brand-primary/30'
                  : 'bg-ui-background-tertiary text-ui-text-secondary border border-transparent hover:bg-ui-background-tertiary'
              } ${loadingGit ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <ProviderIcon provider={provider} className="w-3.5 h-3.5" />
              {providerLabel(provider)}
            </button>
          ))}
        </div>

        {/* Search bar and count - visible when provider is connected or loading */}
        {showConnectedUI && (
          <>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-ui-text-tertiary">
                {isSingleRepo ? 'Select a repository to scan' : 'Select repositories to include'}
              </p>
              <span className="text-sm text-brand-primary dark:text-brand-primaryDarkMode font-medium">
                {selectedRepos.size} selected
              </span>
            </div>

            {providerFilter === 'gitlab' && gitlabConnections.length > 1 && (
              <div className="mb-2">
                <label className="block text-xs text-ui-text-tertiary mb-1">GitLab instance</label>
                <select
                  value={selectedGitlabInstanceUrl ?? ''}
                  onChange={(e) => {
                    const url = e.target.value || undefined;
                    setSelectedGitlabInstanceUrl(url);
                    setRepoCache((prev) => ({ ...prev, gitlab: null }));
                    setGitlabPage(1);
                    setGitlabHasMore(true);
                  }}
                  className="w-full px-3 py-2 text-sm border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary"
                >
                  {gitlabConnections.map((c) => (
                    <option key={c.gitlab_instance_url} value={c.gitlab_instance_url}>
                      {gitlabInstanceDisplayName(c.gitlab_instance_url)} (@{c.gitlab_username})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {providerFilter === 'azure_devops' && adoProjects.length > 0 && (
              <div className="mb-2">
                <label className="block text-xs text-ui-text-tertiary mb-1">Project</label>
                <select
                  value={selectedAdoProject ?? ''}
                  onChange={(e) => handleAdoProjectSelect(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary"
                >
                  {adoProjects.length > 1 && <option value="" disabled>Select a project</option>}
                  {adoProjects.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            )}
            {providerFilter === 'codecommit' && codecommitConnections.length > 1 && (
              <div className="mb-2">
                <label htmlFor="codecommit-connection-picker" className="block text-xs text-ui-text-tertiary mb-1">
                  AWS connection
                </label>
                <select
                  id="codecommit-connection-picker"
                  value={selectedCodecommitConnectionId ?? ''}
                  onChange={(e) => handleCodecommitConnectionSelect(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary"
                >
                  {codecommitConnections.map((c) => (
                    <option key={c.id} value={c.id}>
                      {codecommitConnectionDisplayName(c)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <input
              type="text"
              value={repoSearch}
              onChange={(e) => setRepoSearch(e.target.value)}
              placeholder={providerFilter === 'gitlab' ? 'Search repositories (or use namespace:my-group)' : 'Search repositories'}
              className="w-full px-3 py-2 text-sm border border-ui-border-primary rounded-lg
                         bg-ui-background-secondary text-ui-text-primary
                         placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary
                         focus:ring-2 focus:ring-brand-primary focus:border-transparent mb-2"
            />
          </>
        )}

        {/* Repository list area */}
        {loadingGit || (providerFilter && loadingRepos[providerFilter]) ? (
          <div className="flex items-center justify-center py-8 border border-ui-border-primary rounded-lg">
            <Spinner size={20} />
          </div>
        ) : !isSelectedProviderConnected ? (
          <div className="text-center py-6 border border-ui-border-primary rounded-lg">
            <div className="w-10 h-10 bg-ui-background-tertiary rounded-full flex items-center justify-center mx-auto mb-3">
              <ProviderIcon provider={providerFilter} className="w-5 h-5 text-ui-text-secondary" />
            </div>
            {isFormProvider(providerFilter) ? (
              <>
                <h3 className="text-sm font-medium text-ui-text-primary mb-1">
                  Connect {providerLabel(providerFilter)}
                </h3>
                <p className="text-xs text-ui-text-tertiary mb-3">
                  {providerFilter === 'azure_devops'
                    ? 'Connect your Azure DevOps organization to select repositories.'
                    : providerFilter === 'codecommit'
                      ? 'Connect your AWS account to select CodeCommit repositories.'
                      : `Connect ${providerLabel(providerFilter)} to select repositories.`}
                </p>
                <button
                  type="button"
                  onClick={() => openOrgSettings(organizationId, 'integrations')}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-white text-gray-900 border border-ui-border-primary rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
                >
                  <ProviderIcon provider={providerFilter} className="w-4 h-4" />
                  Connect {providerLabel(providerFilter)}
                </button>
              </>
            ) : (
              <>
                <h3 className="text-sm font-medium text-ui-text-primary mb-1">
                  Connect to {providerLabel(providerFilter)}
                </h3>
                <p className="text-xs text-ui-text-tertiary mb-3">
                  Connect your organization to {providerLabel(providerFilter)} to select repositories.
                </p>
                <button
                  onClick={providerFilter === 'gitlab' ? handleConnectGitlab : handleConnectGithub}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-white text-gray-900 border border-ui-border-primary rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
                >
                  <ProviderIcon provider={providerFilter} className="w-4 h-4" />
                  Connect {providerLabel(providerFilter)}
                </button>
              </>
            )}
          </div>
        ) : filteredRepos.length === 0 ? (
          renderEmptyRepoState()
        ) : (
          <div className="max-h-48 overflow-y-auto space-y-1.5 border border-ui-border-primary rounded-lg p-2">
              <>
                {filteredRepos.map((repo) => {
                  const repoKey = getRepositoryKey(repo.provider, repo.full_name);
                  const isSelected = selectedRepos.has(repoKey);
                  return (
                    <button
                      key={repoKey}
                      onClick={() => toggleRepo(repo.provider, repo.full_name)}
                      className={`w-full text-left p-2.5 rounded-lg border transition-colors flex items-center justify-between ${
                        isSelected
                          ? 'border-brand-primary bg-brand-primary/10 dark:bg-brand-primaryDarkMode/10'
                          : 'border-ui-border-primary hover:bg-ui-background-tertiary'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <ProviderIcon provider={repo.provider} className="w-3.5 h-3.5 text-ui-text-tertiary shrink-0" />
                          <span className="text-sm font-medium text-ui-text-primary truncate">
                            {repo.full_name}
                          </span>
                          <span className="text-xs px-1.5 py-0.5 rounded border border-ui-border-primary text-ui-text-tertiary shrink-0">
                            {repo.private ? 'Private' : 'Public'}
                          </span>
                        </div>
                        <div className="text-xs text-ui-text-tertiary mt-0.5">
                          {repo.default_branch}
                        </div>
                      </div>
                      {isSelected && (
                        <Check className="w-4 h-4 text-brand-primary dark:text-brand-primaryDarkMode shrink-0 ml-2" />
                      )}
                    </button>
                  );
                })}
                {/* Sentinel div for GitLab infinite scroll */}
                {providerFilter === 'gitlab' && gitlabHasMore && (
                  <div ref={loadMoreRef} className="py-2 text-center">
                    {isLoadingMore && (
                      <Spinner size={16} className="mx-auto" />
                    )}
                  </div>
                )}
              </>
          </div>
        )}
      </>
    );
  };

  const renderPublicRepositorySection = () => {
    return (
      <div className="space-y-3">
        <div>
          <label
            htmlFor="public-github-repo"
            className="block text-sm font-medium text-ui-text-secondary mb-1"
          >
            Repository <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="public-github-repo"
            value={publicRepoInput}
            onChange={(e) => setPublicRepoInput(e.target.value)}
            placeholder="juice-shop/juice-shop or https://github.com/juice-shop/juice-shop"
            className="w-full px-3 py-2 text-sm border border-ui-border-primary rounded-lg
                       bg-ui-background-secondary text-ui-text-primary
                       placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary
                       focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
        </div>
        <div>
          <label
            htmlFor="public-github-branch"
            className="block text-sm font-medium text-ui-text-secondary mb-1"
          >
            Default branch
          </label>
          <input
            type="text"
            id="public-github-branch"
            value={publicRepoBranch}
            onChange={(e) => setPublicRepoBranch(e.target.value)}
            placeholder="Use GitHub default branch"
            className="w-full px-3 py-2 text-sm border border-ui-border-primary rounded-lg
                       bg-ui-background-secondary text-ui-text-primary
                       placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary
                       focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {appLimitReached && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Repository limit reached ({quotaBalance?.max_applications}).{' '}
              <button
                onClick={() => openAccountSettings()}
                className="font-medium underline hover:no-underline"
              >
                Upgrade your plan
              </button>{' '}
              to add more repositories.
            </p>
          </div>
        </div>
      )}

      {/* Application Details Section */}
      {!isSingleRepo && (
        <div className="space-y-4">
          <div>
            <label
              htmlFor="app-name"
              className="block text-sm font-medium text-ui-text-secondary mb-1"
            >
              Repository Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="app-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Repository"
              className="w-full px-4 py-2 border border-ui-border-primary rounded-lg
                         bg-ui-background-secondary text-ui-text-primary
                         placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary
                         focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              autoFocus={autoFocus}
            />
          </div>

          <div>
            <label
              htmlFor="app-description"
              className="block text-sm font-medium text-ui-text-secondary mb-1"
            >
              Description
            </label>
            <textarea
              id="app-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description of this repository..."
              rows={2}
              className="w-full px-4 py-2 border border-ui-border-primary rounded-lg
                         bg-ui-background-secondary text-ui-text-primary
                         placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary
                         focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none"
            />
          </div>
        </div>
      )}

      {/* Divider */}
      {!isSingleRepo && <div className="border-t border-ui-border-primary" />}

      {/* Repository Selection Section */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-ui-text-secondary">
          {isSingleRepo ? 'Repository' : 'Repositories'}
        </h3>
        {isAdmin && (
          <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-ui-background-tertiary">
            <button
              type="button"
              onClick={() => {
                setCreationMode('connected');
                setError(null);
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                creationMode === 'connected'
                  ? 'bg-ui-background-secondary text-ui-text-primary shadow-sm'
                  : 'text-ui-text-secondary hover:text-ui-text-primary'
              }`}
            >
              Connected repositories
            </button>
            <button
              type="button"
              onClick={() => {
                setCreationMode('public_github');
                setError(null);
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                creationMode === 'public_github'
                  ? 'bg-ui-background-secondary text-ui-text-primary shadow-sm'
                  : 'text-ui-text-secondary hover:text-ui-text-primary'
              }`}
            >
              Public GitHub repository
            </button>
          </div>
        )}
        {isPublicRepoMode ? renderPublicRepositorySection() : renderRepositorySection()}
      </div>

      {/* Actions */}
      <div className="flex justify-end items-center gap-3 pt-4 border-t border-ui-border-primary">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-ui-text-secondary
                       hover:bg-ui-background-tertiary rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            {cancelLabel}
          </button>
        )}
        <button
          onClick={handleSubmit}
          disabled={
            isSubmitting ||
            (!isSingleRepo && !name.trim()) ||
            appLimitReached ||
            (isPublicRepoMode ? !publicRepoInput.trim() : selectedRepos.size === 0)
          }
          className="px-4 py-2 text-sm font-medium text-black bg-brand-primary hover:bg-brand-primaryDark 
                     disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors
                     flex items-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Spinner size={16} tone="current" />
              Creating...
            </>
          ) : (
            <>
              <Server className="w-4 h-4" />
              {submitLabel}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ApplicationForm;
